import type { Translations } from '@/i18n/locales/en';
import type { Promotion } from '@restaurant/api-client';
import type { PromotionChange, PromotionConflict, PromotionValue } from '@restaurant/api-contracts';
import { valueOfPromotion } from './promotion-summary';

export type PromotionDraftStatus = 'new' | 'updated' | 'archived' | 'restored' | null;

export interface PromotionDraftRow {
  id: string;
  value: PromotionValue;
  before?: PromotionValue;
  status: PromotionDraftStatus;
  promotion: Promotion | null;
  conflicts: PromotionConflict[];
}

export interface PromotionsDraft {
  rows: PromotionDraftRow[];
  targets: string[];
}

type SummaryCopy = Translations['promotionsQuickEdit']['summary'];

const valueOfCreated = ({
  kind: _kind,
  promotionId: _id,
  ...value
}: Extract<PromotionChange, { kind: 'promotionCreated' }>): PromotionValue => value;

export function buildPromotionsDraft(
  listed: Promotion[],
  all: Promotion[],
  changes: PromotionChange[],
  conflicts: PromotionConflict[] = [],
): PromotionsDraft {
  const known = new Map([...all, ...listed].map((promotion) => [promotion.id, promotion]));
  const row = (promotion: Promotion): PromotionDraftRow => ({
    id: promotion.id,
    value: valueOfPromotion(promotion),
    status: null,
    promotion,
    conflicts: [],
  });
  const created: PromotionDraftRow[] = [];
  const rows = new Map(listed.map((promotion) => [promotion.id, row(promotion)]));
  for (const change of changes) {
    if (change.kind === 'promotionCreated') {
      created.push({
        id: change.promotionId,
        value: valueOfCreated(change),
        status: 'new',
        promotion: null,
        conflicts: [],
      });
      continue;
    }
    const promotion = known.get(change.promotionId);
    const current = rows.get(change.promotionId) ?? (promotion ? row(promotion) : null);
    if (change.kind === 'promotionUpdated') {
      rows.set(change.promotionId, {
        id: change.promotionId,
        value: change.after,
        before: change.before,
        status: 'updated',
        promotion: promotion ?? null,
        conflicts: [],
      });
    } else if (current) {
      rows.set(change.promotionId, {
        ...current,
        status: change.after ? 'archived' : 'restored',
      });
    }
  }
  const ordered = [...created, ...rows.values()].map((draftRow) => ({
    ...draftRow,
    conflicts: conflicts.filter((conflict) => conflict.promotionId === draftRow.id),
  }));
  return {
    rows: ordered,
    targets: ordered.flatMap((draftRow) => (draftRow.status === null ? [] : [draftRow.id])),
  };
}

export function promotionChangeLabels(all: Promotion[], changes: PromotionChange[]): string[] {
  const names = new Map(all.map((promotion) => [promotion.id, promotion.name.en ?? '']));
  return changes.map((change) =>
    change.kind === 'promotionCreated'
      ? (change.name.en ?? '')
      : change.kind === 'promotionUpdated'
        ? (change.after.name.en ?? '')
        : (names.get(change.promotionId) ?? ''),
  );
}

export function summarizePromotionsDraft(changes: PromotionChange[], copy: SummaryCopy): string {
  const count = (kind: PromotionChange['kind']) =>
    changes.filter((change) => change.kind === kind).length;
  const created = count('promotionCreated');
  const updated = count('promotionUpdated');
  const archivals = changes.filter((change) => change.kind === 'promotionArchived');
  const archived = archivals.filter((change) => change.after).length;
  const restored = archivals.length - archived;
  return [
    ...(created ? [copy.created(created)] : []),
    ...(updated ? [copy.updated(updated)] : []),
    ...(archived ? [copy.archived(archived)] : []),
    ...(restored ? [copy.restored(restored)] : []),
  ].join(', ');
}
